import styled, { css } from "styled-components";

export const previewTheme = {
  indigo: "#4f46e5",
  indigoSoft: "#eef2ff",
  indigoBorder: "#7468fd",
  ink: "#17143f",
  body: "#706f7b",
  muted: "#858490",
  surface: "#ffffff",
  canvas: "#f8f9fc",
  hairline: "#e5e7eb",
  green: "#1a8245",
  red: "#d32f2f",
};

/**
 * Typography presets shared by a field's read-only display and its editor, so
 * switching between them never changes the text's size, weight or colour.
 */
export type PreviewFieldVariant =
  | "headline"
  | "title"
  | "body"
  | "caption"
  | "buttonLabel";

const typography: Record<PreviewFieldVariant, ReturnType<typeof css>> = {
  headline: css`
    color: ${previewTheme.indigo};
    font-size: 18px;
    font-weight: 600;
    line-height: 1.5;
    min-height: 27px;
  `,
  title: css`
    color: ${previewTheme.ink};
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    min-height: 24px;
  `,
  body: css`
    color: ${previewTheme.body};
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    min-height: 22px;
  `,
  caption: css`
    color: ${previewTheme.muted};
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    min-height: 18px;
  `,
  buttonLabel: css`
    color: ${previewTheme.surface};
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
    text-align: center;
    min-height: 24px;
  `,
};

export const StyledPreviewShell = styled.div`
  background-color: ${previewTheme.canvas};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

export const StyledLiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: ${previewTheme.indigoSoft};
  color: ${previewTheme.indigo};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 16px;
`;

export const StyledPreviewTitle = styled.h2`
  color: ${previewTheme.ink};
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px;
`;

export const StyledPreviewIntro = styled.p`
  color: ${previewTheme.body};
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 24px;
  max-width: 640px;

  strong {
    color: ${previewTheme.ink};
    font-weight: 600;
  }
`;

/* Marks values that come from the order rather than from customization. */
export const StyledPreviewDataTag = styled.span`
  position: absolute;
  top: -10px;
  right: 12px;
  background-color: #eceef3;
  color: ${previewTheme.muted};
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
`;

/**
 * Owns the spacing between fields so the validation message below a field
 * sits in its own flow instead of inside the next field's margin, where the
 * next label pill (top: -9px) would overlap it.
 */
export const StyledFieldWrapper = styled.div`
  margin-bottom: 20px;
`;

/**
 * Pencil badge revealed on hover. Purely decorative — the whole field is the
 * click target, so this is aria-hidden and ignores pointer events rather than
 * nesting a second interactive element inside the field's role="button".
 * Declared before the containers so they can reference it as a selector.
 */
export const StyledEditAffordance = styled.span<{ $onDark?: boolean }>`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $onDark }) =>
    $onDark ? "rgba(255, 255, 255, 0.25)" : previewTheme.indigo};
  color: ${previewTheme.surface};
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease-in-out;

  i {
    font-size: 14px;
  }
`;

/* Hover/focus rules every editable container shares. */
export const editableContainer = css`
  &:hover ${StyledEditAffordance} {
    opacity: 1;
  }

  /* Once the editor is open the badge is redundant. Must follow the hover rule. */
  &:focus-within ${StyledEditAffordance} {
    opacity: 0;
  }
`;

/* Dashed affordance marking a field as editable. */
export const StyledEditableField = styled.div<{ $invalid?: boolean }>`
  position: relative;
  border: 1px dashed
    ${({ $invalid }) =>
      $invalid ? previewTheme.red : previewTheme.indigoBorder};
  border-radius: 10px;
  /* Right padding reserves room for the hover badge so text never runs under it. */
  padding: 16px 52px 12px 12px;
  cursor: text;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${previewTheme.indigoSoft};
  }

  &:focus-within {
    background-color: ${previewTheme.surface};
    border-style: solid;
  }

  ${editableContainer};
`;

export const StyledEditableLabel = styled.span`
  position: absolute;
  top: -9px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${previewTheme.indigoSoft};
  color: ${previewTheme.indigo};
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;

  i {
    font-size: 10px;
  }
`;

/**
 * All editors are textareas, not inputs: a single-line input scrolls long text
 * horizontally out of view, which reads as a length limit. Textareas wrap and
 * are grown to fit by the auto-resize handler in EditableField.
 */
export const StyledEditorInput = styled.textarea<{
  $variant: PreviewFieldVariant;
}>`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  box-shadow: none;
  font-family: inherit;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;

  &:focus,
  &:enabled:focus,
  &:hover,
  &:enabled:hover {
    border: none;
    outline: none;
    box-shadow: none;
  }

  ${({ $variant }) => typography[$variant]};
`;

/* min-height (from the typography preset) keeps a cleared field clickable. */
export const StyledDisplayText = styled.p<{ $variant: PreviewFieldVariant }>`
  && {
    ${({ $variant }) => typography[$variant]};
  }
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const StyledTokenChip = styled.code`
  background-color: ${previewTheme.indigoSoft};
  color: ${previewTheme.indigo};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

export const StyledFieldError = styled.div`
  margin-top: 6px;
  line-height: 1.4;
`;

/* ---- Settings cards: the DISPLAY SETTINGS pattern shared by the tabs. ---- */

/* Mirrors the checkout tab's DISPLAY SETTINGS card. */
export const StyledSettingsCard = styled.div`
  background-color: ${previewTheme.surface};
  border: 1px solid ${previewTheme.hairline};
  border-radius: 16px;
  padding: 24px;
`;

export const StyledSettingsHeader = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${previewTheme.ink};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin: 0 0 8px;

  i {
    color: ${previewTheme.indigo};
    font-size: 16px;
  }
`;

export const StyledSettingsDescription = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 8px;
`;

export const StyledSettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-top: 1px solid ${previewTheme.hairline};

  &:first-of-type {
    border-top: none;
  }

  /* The text takes the slack; without this the switch shrinks and its pill clips. */
  > div:first-child {
    min-width: 0;
  }

  .p-inputswitch {
    flex: none;
  }
`;

/* A span, not a p: it sits inside the row's <label>, which takes phrasing content. */
export const StyledSettingTitle = styled.span`
  display: block;
  color: ${previewTheme.ink};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 2px;
`;

export const StyledSettingSubtitle = styled.p`
  && {
    color: ${previewTheme.body};
  }
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
`;

/* Footnote at the bottom of a settings card. */
export const StyledSettingsNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-top: 1px solid ${previewTheme.hairline};
  padding-top: 16px;
  color: ${previewTheme.body};
  font-size: 13px;
  line-height: 1.5;

  i {
    color: ${previewTheme.indigo};
    font-size: 13px;
    margin-top: 2px;
  }
`;

/**
 * Keeps a settings card that has no preview beside it the same width as one
 * that does, so cards stacked down a tab line up instead of stretching.
 */
export const StyledSettingsColumn = styled.div`
  max-width: 520px;
`;
